import React from "react";

/** Option row for MUI Autocomplete (matches Analytics / onboarding `{ id, name, index }`). */
export type CalendarGroupOption = {
  id: string | number;
  name: string;
  index: number;
};

export function normalizeCalendarGroupSearch(s: string) {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

type ParentAccountLike = { id: string | number; name: string };

/**
 * Stable group search for Calendar Group Autocomplete: same approach as onboarding
 * `ChoiceFieldType` — MUI's default filter can see an empty `inputValue` while typing
 * when `value` is null; we filter in React from controlled `groupFilterInput`.
 */
export function useCalendarGroupAutocomplete(
  parentAccounts: ParentAccountLike[] | null | undefined,
  selectedGroupId: string | number | null | undefined
) {
  const groupOptionRows = React.useMemo((): CalendarGroupOption[] => {
    if (!parentAccounts?.[0]) {
      return [];
    }
    return parentAccounts.map((object, index: number) => ({
      id: object.id,
      name: String(object.name ?? ""),
      index,
    }));
  }, [parentAccounts]);

  const [groupFilterInput, setGroupFilterInput] = React.useState("");

  const filteredGroupOptions = React.useMemo(() => {
    const q = normalizeCalendarGroupSearch(groupFilterInput);
    if (!q) {
      return groupOptionRows;
    }
    return groupOptionRows.filter((opt) => {
      const n = normalizeCalendarGroupSearch(String(opt.name));
      return n.includes(q);
    });
  }, [groupFilterInput, groupOptionRows]);

  React.useEffect(() => {
    const gid = selectedGroupId;
    if (gid === "" || gid == null) {
      return;
    }
    const row = groupOptionRows.find((r) => String(r.id) === String(gid));
    if (row) {
      setGroupFilterInput(String(row.name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when selection id or row count changes; `groupOptionRows` omitted (parent may pass new array ref each render).
  }, [selectedGroupId, groupOptionRows.length]);

  const onGroupInputChange = React.useCallback(
    (_event: unknown, value: string, reason: string) => {
      if (reason === "input" || reason === "clear") {
        setGroupFilterInput(reason === "clear" ? "" : value);
      } else if (reason === "reset") {
        if (typeof value === "string" && value !== "") {
          setGroupFilterInput(value);
        }
      }
    },
    []
  );

  const clearGroupFilterInput = React.useCallback(() => {
    setGroupFilterInput("");
  }, []);

  const filterOptionsIdentity = React.useCallback(
    (opts: CalendarGroupOption[]) => opts,
    []
  );

  return {
    filteredGroupOptions,
    groupFilterInput,
    onGroupInputChange,
    clearGroupFilterInput,
    filterOptionsIdentity,
  };
}
